/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react"

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom"

// @mui material components
import { ThemeProvider } from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"
// import Icon from "@mui/material/Icon"

// Material Dashboard 2 React components
// import MDBox from "components/MDBox"

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav"
import Configurator from "examples/Configurator"

// Material Dashboard 2 React themes
import theme from "assets/theme"
import themeRTL from "assets/theme/theme-rtl"

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark"
import themeDarkRTL from "assets/theme-dark/theme-rtl"

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl"
import { CacheProvider } from "@emotion/react"
import createCache from "@emotion/cache"

// Material Dashboard 2 React routes
import routes from "routes"

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav } from "context"

// Images
import brandWhite from "assets/images/logo-ct.png"
import brandDark from "assets/images/logo-ct-dark.png"
import { RecoilRoot } from "recoil"
import * as authService from "./services/auth/auth.service"

export default function App() {
  const [controller, dispatch] = useMaterialUIController()
  const { miniSidenav, direction, layout, sidenavColor, transparentSidenav, whiteSidenav, darkMode } = controller
  const [onMouseEnter, setOnMouseEnter] = useState(false)
  const [rtlCache, setRtlCache] = useState(null)
  const { pathname } = useLocation()
  const navigate = useNavigate()

  console.log("REACT_APP_BASE_URL ====>", process.env.REACT_APP_BASE_URL)

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    })

    setRtlCache(cacheRtl)
  }, [])

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false)
      setOnMouseEnter(true)
    }
  }

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true)
      setOnMouseEnter(false)
    }
  }

  // Change the openConfigurator state
  // const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator)

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction)
  }, [direction])

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0
    document.scrollingElement.scrollTop = 0
  }, [pathname])

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse)
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />
      }

      return null
    })

  // const configsButton = (
  //   <MDBox
  //     display="flex"
  //     justifyContent="center"
  //     alignItems="center"
  //     width="3.25rem"
  //     height="3.25rem"
  //     bgColor="white"
  //     shadow="sm"
  //     borderRadius="50%"
  //     position="fixed"
  //     right="2rem"
  //     bottom="2rem"
  //     zIndex={99}
  //     color="dark"
  //     sx={{ cursor: "pointer" }}
  //     onClick={handleConfiguratorOpen}
  //   >
  //     <Icon fontSize="small" color="inherit">
  //       settings
  //     </Icon>
  //   </MDBox>
  // )

  useEffect(() => {
    if (!localStorage.getItem("resuxi-token") && !window.location.pathname.includes("reset-password")) {
      navigate("/sign-in")
    }
  }, [localStorage.getItem("resuxi-token")])
  useEffect(() => {
    if (window.location.pathname === "/revenus" && localStorage.getItem("resuxi-role") !== "superAdmin") {
      window.location.pathname = "colis"
    }
    if (localStorage.getItem("resuxi-token") && !localStorage.getItem("resuxi-role")) {
      getMe()
    }
  }, [localStorage.getItem("resuxi-token")])

  const getMe = async () => {
    let body = {
      token: localStorage.getItem("resuxi-token"),
    }
    try {
      const result = await authService.getMe(body)
      if (result) {
        if (result.response && result.response.data) {
          if (result.response.data.status === 400) {
            console.log("error result ===>", result)
          }
        }
        if (result.data && result.data.status === 200) {
          localStorage.setItem("resuxi-role", result.data.data.role)
        }
      }
    } catch (error) {
      console.log("error ===>", error)
    }
  }

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <RecoilRoot>
        <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
          <CssBaseline />
          {localStorage.getItem("resuxi-token") && layout === "dashboard" && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                brandName="Resuxi Shop"
                routes={routes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
              <Configurator />
              {/* {configsButton} */}
            </>
          )}
          {layout === "vr" && <Configurator />}
          <Routes>
            {getRoutes(routes)}
            {localStorage.getItem("resuxi-token") ? (
              <Route path="*" element={<Navigate to="/colis" />} />
            ) : (
              <Route path="*" element={<Navigate to="/sign-in" />} />
            )}
          </Routes>
        </ThemeProvider>
      </RecoilRoot>
    </CacheProvider>
  ) : (
    <RecoilRoot>
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        {localStorage.getItem("resuxi-token") && layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="Resuxi Shop"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {/* {configsButton} */}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          {localStorage.getItem("resuxi-token") ? (
            <Route path="*" element={<Navigate to="/colis" />} />
          ) : (
            <Route path="*" element={<Navigate to="/sign-in" />} />
          )}
        </Routes>
      </ThemeProvider>
    </RecoilRoot>
  )
}
